import React, { FC } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { ButtonType } from '@nhl/nhl-breakaway-ui';

import { State } from '~/core/types';
import franchiseSelectors from '~/redux/ducks/config/selectors/franchiseSelectors';
import { commonMessages } from '~/utility/commonMessages';
import { GAME_TYPE_IDS } from '~/utility/constants';

import { Form, FilterSelect, FilterSubmit } from './styles';

type Props = {
    seasonId?: number | null;
    gameTypeId?: number | null;
    franchiseId?: number | null;
    onFilterChange: (filterName: any) => (e: any, value: any) => void;
    onFilterSubmit: (e: any) => void;
    isSubmitDisabled: boolean;
};

const Filter: FC<Props> = ({ seasonId, gameTypeId, franchiseId, onFilterChange, onFilterSubmit, isSubmitDisabled }) => {
    const intl = useIntl();
    const seasonsMap = useSelector((state: State) => state.config.seasonData);
    const franchises = useSelector((state: State) => state.config.franchiseData);
    const currentSeasonInfo = useSelector((state: State) => state.config.fieldDefaultData);

    const seasonOptions =
        seasonsMap &&
        Object.keys(seasonsMap)
            .reverse()
            .map((season) => ({
                key: season,
                label: hockeyFormatter.singleSeasonFormatter(season),
                value: Number(season),
            }));

    const seasonValue = seasonOptions && seasonOptions.find((obj) => obj.value === seasonId);

    // GameType options & selected value
    const regularSeasonEndDate = seasonValue?.value && seasonsMap && seasonsMap[seasonValue.value].regularSeasonEndDate;

    const includePlayoffs = regularSeasonEndDate && DateTime.fromISO(regularSeasonEndDate) > DateTime.local();
    const tempOptions = GAME_TYPE_IDS.map((id) => ({
        key: id,
        value: id,
        label: intl.formatMessage(commonMessages.gameType[id]),
    }));
    const gameTypeOptions =
        currentSeasonInfo && includePlayoffs
            ? tempOptions.filter((obj) => obj.value <= currentSeasonInfo.gameTypeId)
            : tempOptions;

    const gameTypeValue = gameTypeOptions.find((obj) => obj.value === gameTypeId);

    // Franchise options & selected value
    const franchiseOptions = [
        { value: 0, label: intl.formatMessage(commonMessages.labels.allFranchises) },
        ...franchiseSelectors.getFranchisesForSeason(franchises, seasonValue?.value).map((franchise) => ({
            key: franchise.id,
            value: franchise.id,
            label: franchise.fullName,
        })),
    ];
    const franchiseValue = franchiseOptions.find((obj) => obj.value === franchiseId);

    return (
        <Form onSubmit={onFilterSubmit}>
            {seasonOptions && (
                <FilterSelect
                    options={seasonOptions}
                    onOptionChange={onFilterChange('seasonId')}
                    selectedOption={seasonValue}
                    isSearchEnabled
                    optionsBoxAriaLabel={intl.formatMessage(commonMessages.ariaLabels.season.optionsBox)}
                    dropdownButtonAriaLabel={intl.formatMessage(commonMessages.ariaLabels.season.dropdownButton)}
                />
            )}
            <FilterSelect
                options={gameTypeOptions}
                onOptionChange={onFilterChange('gameTypeId')}
                selectedOption={gameTypeValue}
                isSearchEnabled
                optionsBoxAriaLabel={intl.formatMessage(commonMessages.ariaLabels.gameType.optionsBox)}
                dropdownButtonAriaLabel={intl.formatMessage(commonMessages.ariaLabels.gameType.dropdownButton)}
            />
            <FilterSelect
                options={franchiseOptions}
                onOptionChange={onFilterChange('franchiseId')}
                selectedOption={franchiseValue}
                isSearchEnabled
                optionsBoxAriaLabel={intl.formatMessage(commonMessages.ariaLabels.franchise.optionsBox)}
                dropdownButtonAriaLabel={intl.formatMessage(commonMessages.ariaLabels.franchise.dropdownButton)}
            />
            <FilterSubmit type="submit" variant={ButtonType.CTA} disabled={isSubmitDisabled}>
                <FormattedMessage {...commonMessages.labels.go} />
            </FilterSubmit>
        </Form>
    );
};

export default Filter;
